import styled from 'styled-components'
import palette from '../../../theme/palette';
import { ResourceHubElementWithColorSchemeProps } from '../../../models/organisms/ResourceHub';
import { Icon } from '../../atoms/Icon/Icon.styles';

export const FilterSelectWrapper = styled.div`
  position: relative;
`

export const FilterSelectTrigger = styled.button<{ $isOpen: boolean; $hasSelection: boolean; } & ResourceHubElementWithColorSchemeProps>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.rem(8)};
  padding: ${({ theme }) => `${theme.rem(10)} ${theme.rem(12)}`};
  border-radius: ${({ theme }) => theme.rem(4)};
  border: 1px solid ${({ theme, $hasSelection, colorScheme }) =>
    $hasSelection ? theme.colorScheme[colorScheme].tabs.selected.bgPressed : theme.colorScheme[colorScheme].icon.base.tertiary};
  background: ${({ theme, $hasSelection, colorScheme }) =>
    $hasSelection ? theme.colorScheme[colorScheme].tabs.selected.bgPressed : 'transparent'};
  font-size: ${({ theme }) => theme.rem(16)};
  line-height: ${({ theme }) => theme.rem(24)};
  color: ${({ theme, $hasSelection, colorScheme }) =>
    $hasSelection ? theme.colorScheme[colorScheme].surface.text.base.primary : theme.colorScheme[colorScheme].icon.base.tertiary};
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].tabs.selected.bgPressed};
    background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].tabs.selected.bgPressed};
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.base.primary};
    ${Icon} {
      color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.base.primary};
    }
  }

  svg {
    width: ${({ theme }) => theme.rem(24)};
    height: ${({ theme }) => theme.rem(24)};
  }

  i[aria-label='chevron down'] {
    transition: transform 0.2s ease;
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};

    svg {
      width: ${({ theme }) => theme.rem(18)};
      height: ${({ theme }) => theme.rem(18)};
    }
  }
`

export const FilterSelectSelectedCount = styled.span<ResourceHubElementWithColorSchemeProps>`
  font-size: ${({ theme }) => theme.rem(12)};
  width: ${({ theme }) => theme.rem(20)};
  height: ${({ theme }) => theme.rem(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.accent.base};
  color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].surface.text.base.negative};
  border-radius: 50%;
  position: relative;
  margin-left: ${({ theme }) => theme.rem(8)};

  &:before {
    content: '';
    width: 0;
    height: 100%;
    border-left: 1px solid ${palette.grayscale[50]};
    position: absolute;
    left: -8px;
  }
`

export const FilterSelectDropdown = styled.div`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.rem(4)});
  left: 0;
  z-index: 10;
  min-width: ${({ theme }) => theme.rem(260)};
  border-radius: ${({ theme }) => theme.rem(8)};
  background: ${palette.grayscale[50]};
  border: 1px solid ${palette.grayscale[100]};
  box-shadow: 0 2px 12px 0 rgba(0, 34, 33, 0.10);
  padding: ${({ theme }) => `${theme.rem(16)} 0`};
  max-height: ${({ theme }) => theme.rem(370)};
  overflow: auto;
`

export const FilterSelectHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `0 ${theme.rem(16)} ${theme.rem(12)}`};
  border-bottom: 1px solid ${palette.grayscale[100]};
`

export const FilterSelectTitle = styled.span`
  font-size: ${({ theme }) => theme.rem(16)};
  line-height: ${({ theme }) => theme.rem(24)};
  font-weight: 400;
  color: ${palette.green[300]};
`

export const FilterSelectClear = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: ${({ theme }) => theme.rem(12)};
  font-weight: 400;
  text-transform: uppercase;
  color: ${palette.green[300]};
  cursor: pointer;
  letter-spacing: 0.5px;

  &:hover {
    text-decoration: underline;
  }
`

export const FilterSelectOption = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.rem(10)};
  padding: ${({ theme }) => `${theme.rem(10)} ${theme.rem(16)}`};
  cursor: pointer;
  font-size: ${({ theme }) => theme.rem(14)};
  color: ${palette.green[300]};
  transition: background 0.15s ease;

  &:hover {
    background: ${palette.grayscale[100]};
  }

  svg {
    width: ${({ theme }) => theme.rem(20)};
    height: ${({ theme }) => theme.rem(20)};
    color: ${palette.green[300]};
  }
`

export const FilterSelectCheckbox = styled.span<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.rem(18)};
  height: ${({ theme }) => theme.rem(18)};
  flex-shrink: 0;
  border: 1.5px solid ${({ theme, $checked }) =>
    $checked ? palette.green[300] : palette.grayscale[400]};
  border-radius: ${({ theme }) => theme.rem(3)};
  background: ${({ theme, $checked }) =>
    $checked ? palette.green[300] : 'transparent'};
  transition: all 0.15s ease;

  &::after {
    content: '';
    display: ${({ $checked }) => ($checked ? 'block' : 'none')};
    width: ${({ theme }) => theme.rem(5)};
    height: ${({ theme }) => theme.rem(9)};
    border: solid ${palette.grayscale[50]};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) translate(-1px, -1px);
  }
`

export const FilterSelectOptionLabel = styled.span`
  flex: 1;
`

export const FilterSelectDivider = styled.div`
  height: 1px;
  background: ${palette.grayscale[100]};
  margin: ${({ theme }) => `${theme.rem(4)} 0`};
`

export const FilterSelectCheckboxInput = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`
